import { omit, findIndex } from 'lodash';
import { SEARCH_CLEAR_RESULTS, SEARCH } from '../actions/search';
import { FETCH_SEARCH_SUCCESS, FETCH_SEARCH_FAILURE } from '../actions/fetch-search';
import { SUBSCRIBE_REQUEST, SUBSCRIBE_FAILURE } from '../actions/subscribe';
import { UNSUBSCRIBE_REQUEST, UNSUBSCRIBE_FAILURE } from '../actions/unsubscribe';
import { UPDATE_USER_SUCCESS } from '../actions/update-user';
import { PROMOTE_WRITER_SUCCESS } from '../actions/promote-writer';
import { DEMOTE_WRITER_SUCCESS } from '../actions/demote-writer';
import {
  INCREMENT_POST_LIKE_COUNT_SUCCESS,
  INCREMENT_POST_LIKE_COUNT_FAILURE,
  INCREMENT_POST_LIKE_COUNT_REQUEST,
} from '../actions/increment-post-like-count';
import {
  INCREMENT_COMMENT_LIKE_COUNT_SUCCESS,
  INCREMENT_COMMENT_LIKE_COUNT_FAILURE,
  INCREMENT_COMMENT_LIKE_COUNT_REQUEST,
} from '../actions/increment-comment-like-count';
import { PIN_POST_SUCCESS } from '../actions/pin-post';
import { UNPIN_POST_SUCCESS } from '../actions/unpin-post';
import { ENABLE_POST_COMMENTS_SUCCESS } from '../actions/enable-post-comments';
import { DISABLE_POST_COMMENTS_SUCCESS } from '../actions/disable-post-comments';
import { DELETE_POST_SUCCESS } from '../actions/delete-post';
import { DELETE_COMMENT_SUCCESS } from '../actions/delete-comment';
import { USER_SET_PERMISSIONS } from '../actions/user';
import { BLOCK_USER_SUCCESS } from '../actions/block-user';
import { UNBLOCK_USER_SUCCESS } from '../actions/unblock-user';

const initialState = {
  lastSearchUuid: undefined,
  posts: [],
  members: [],
};

// eslint-disable-next-line complexity
const post = (state, action) => {
  switch (action.type) {
    case PIN_POST_SUCCESS:
    case UNPIN_POST_SUCCESS:
    case ENABLE_POST_COMMENTS_SUCCESS:
    case DISABLE_POST_COMMENTS_SUCCESS:
      if (state._id === action.payload._id) {
        return { ...action.payload, type: 'post' };
      }
      return state;
    case INCREMENT_POST_LIKE_COUNT_SUCCESS:
    case INCREMENT_COMMENT_LIKE_COUNT_SUCCESS:
      if (state._id === action.payload._id) {
        return omit({ ...state, ...action.payload }, 'isLikeInProgress');
      }
      if (state.postId === action.payload._id) {
        return omit({ ...state, post: { ...state.post, ...action.payload } }, 'post.isLikeInProgress');
      }
      return state;
    case INCREMENT_COMMENT_LIKE_COUNT_REQUEST:
    case INCREMENT_POST_LIKE_COUNT_REQUEST: {
      if (state._id === action.payload._id) {
        return {
          ...state,
          isLiked: !state.isLiked,
          likeCount: state.likeCount + (state.isLiked ? -1 : 1),
          isLikeInProgress: true,
        };
      }
      if (state.postId === action.payload._id) {
        return {
          ...state,
          post: {
            ...state.post,
            isLiked: !state.post.isLiked,
            likeCount: state.post.likeCount + (state.post.isLiked ? -1 : 1),
            isLikeInProgress: true,
          },
        };
      }
      return state;
    }
    case INCREMENT_COMMENT_LIKE_COUNT_FAILURE:
    case INCREMENT_POST_LIKE_COUNT_FAILURE:
      if (state._id === action.payload._id) {
        return omit(
          {
            ...state,
            isLiked: !state.isLiked,
            likeCount: state.likeCount + (state.isLiked ? -1 : 1),
          },
          'isLikeInProgress',
        );
      }
      if (state.postId === action.payload._id) {
        return omit(
          {
            ...state,
            post: {
              ...state.post,
              isLiked: !state.post.isLiked,
              likeCount: state.post.likeCount + (state.post.isLiked ? -1 : 1),
            },
          },
          'post.isLikeInProgress',
        );
      }
      return state;
    case PROMOTE_WRITER_SUCCESS:
    case DEMOTE_WRITER_SUCCESS:
    case USER_SET_PERMISSIONS:
    case UPDATE_USER_SUCCESS:
    case BLOCK_USER_SUCCESS:
    case UNBLOCK_USER_SUCCESS:
      if (state.owner._id === action.payload._id) {
        return { ...state, owner: action.payload };
      }
      return state;
    default:
      return state;
  }
};

const member = (state, action) => {
  switch (action.type) {
    case PROMOTE_WRITER_SUCCESS:
    case DEMOTE_WRITER_SUCCESS:
    case USER_SET_PERMISSIONS:
    case UPDATE_USER_SUCCESS:
    case BLOCK_USER_SUCCESS:
    case UNBLOCK_USER_SUCCESS:
      if (state._id === action.payload._id) {
        return action.payload;
      }
      return state;
    default:
      return state;
  }
};

const renameUsersKey = data => {
  const { users, ...result } = data;
  result.members = users;

  return result;
};

// eslint-disable-next-line complexity
export default (state = initialState, action) => {
  switch (action.type) {
    case SEARCH:
      return { ...state, query: action.payload };
    case FETCH_SEARCH_SUCCESS:
      return { ...state, ...renameUsersKey(action.payload), posts: action.payload.posts };
    case FETCH_SEARCH_FAILURE:
    case SEARCH_CLEAR_RESULTS:
      return { ...state, posts: [], members: [] };
    case SUBSCRIBE_REQUEST: {
      const index = findIndex(state.members, ['_id', action.payload._id]);
      if (index > -1) {
        const member = state.members[index];
        return {
          ...state,
          members: [
            ...state.members.slice(0, index),
            { ...member, followerCount: member.followerCount + 1, isSubscribed: true },
            ...state.members.slice(index + 1),
          ],
        };
      }
      return state;
    }
    case UNSUBSCRIBE_REQUEST: {
      const index = findIndex(state.members, ['_id', action.payload._id]);
      if (index > -1) {
        const member = state.members[index];
        return {
          ...state,
          members: [
            ...state.members.slice(0, index),
            { ...member, followerCount: member.followerCount - 1, isSubscribed: false },
            ...state.members.slice(index + 1),
          ],
        };
      }
      return state;
    }
    case SUBSCRIBE_FAILURE: {
      const index = findIndex(state.members, ['_id', action.payload._id]);
      if (index > -1) {
        const member = state.members[index];
        return {
          ...state,
          members: [
            ...state.members.slice(0, index),
            { ...member, followerCount: member.followerCount - 1, isSubscribed: false },
            ...state.members.slice(index + 1),
          ],
        };
      }
      return state;
    }
    case UNSUBSCRIBE_FAILURE: {
      const index = findIndex(state.members, ['_id', action.payload._id]);
      if (index > -1) {
        const member = state.members[index];
        return {
          ...state,
          members: [
            ...state.members.slice(0, index),
            { ...member, followerCount: member.followerCount + 1, isSubscribed: true },
            ...state.members.slice(index + 1),
          ],
        };
      }
      return state;
    }
    case PROMOTE_WRITER_SUCCESS:
    case DEMOTE_WRITER_SUCCESS:
    case USER_SET_PERMISSIONS:
    case UPDATE_USER_SUCCESS:
    case BLOCK_USER_SUCCESS:
    case UNBLOCK_USER_SUCCESS:
      return {
        ...state,
        posts: state.posts.map(entity => post(entity, action)),
        members: state.members.map(entity => member(entity, action)),
      };
    case DELETE_POST_SUCCESS:
    case DELETE_COMMENT_SUCCESS:
      return { ...state, posts: state.posts.filter(post => post._id !== action.payload) };
    case PIN_POST_SUCCESS:
    case UNPIN_POST_SUCCESS:
    case ENABLE_POST_COMMENTS_SUCCESS:
    case DISABLE_POST_COMMENTS_SUCCESS:
    case INCREMENT_POST_LIKE_COUNT_REQUEST:
    case INCREMENT_COMMENT_LIKE_COUNT_REQUEST:
    case INCREMENT_COMMENT_LIKE_COUNT_FAILURE:
    case INCREMENT_COMMENT_LIKE_COUNT_SUCCESS:
    case INCREMENT_POST_LIKE_COUNT_FAILURE:
    case INCREMENT_POST_LIKE_COUNT_SUCCESS: {
      let newState = { ...state };
      newState.posts.forEach((item, index) => {
        if (item._id === action.payload._id) {
          const currentPost = post(item, action);
          newState = {
            ...newState,
            posts: [...newState.posts.slice(0, index), { ...currentPost }, ...newState.posts.slice(index + 1)],
          };
        }
        if (item.postId === action.payload._id) {
          const currentPost = post(item, action);
          newState = {
            ...newState,
            posts: [...newState.posts.slice(0, index), { ...currentPost }, ...newState.posts.slice(index + 1)],
          };
        }
      });
      return newState;
    }
    default:
      return state;
  }
};
